import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { IonToast } from '@ionic/react';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { enviarBufferTcp, TcpSocketApi } from '../Utils/NetworkPrinter';
import { criarErroImpressao, executarComTimeout, permiteRetryImpressao } from '../Utils/PrintOperation';
import { setPrintQueueInstance } from '../Utils/PrintQueueHelper';
import { processarFilaSequencial } from './PrintQueueProcessor';

interface PrintJob {
    id: string;
    tipo: 'ble' | 'spp' | 'rede' | 'local';
    dados: any;
    comandosEscPos: Uint8Array;
    impressoraConfig: {
        deviceId?: string;
        address?: string;
        ip?: string;
        porta?: number;
        printerName?: string;
    };
    quantidadeVias: number;
    origem: string;
    tentativas?: number;
}

interface PrintQueueContextData {
    adicionarNaFila: (job: Omit<PrintJob, 'id'>) => void;
    filaAtual: PrintJob[];
    imprimindoAgora: string | null;
    limparFila: () => void;
}

const PrintQueueContext = createContext<PrintQueueContextData>({} as PrintQueueContextData);

export const PrintQueueProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const filaImpressao = useRef<PrintJob[]>([]);
    const imprimindoAtualmente = useRef(false);
    const [filaAtual, setFilaAtual] = useState<PrintJob[]>([]);
    const [imprimindoAgora, setImprimindoAgora] = useState<string | null>(null);
    const [alertaImpressao, setAlertaImpressao] = useState<string | null>(null);

    const gerarId = () => `print_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const adicionarNaFila = (job: Omit<PrintJob, 'id'>) => {
        const novoJob: PrintJob = { ...job, id: gerarId(), tentativas: 0 };

        filaImpressao.current.push(novoJob);
        setFilaAtual([...filaImpressao.current]);

        console.log(`📋 [FILA] Adicionado ${job.tipo.toUpperCase()} (${job.origem}):`, {
            id: novoJob.id,
            vias: job.quantidadeVias,
            posicaoFila: filaImpressao.current.length
        });

        processarFilaDeImpressao();
    };

    // Exporta adicionarNaFila via helper global ao montar o provider
    useEffect(() => {
        setPrintQueueInstance(adicionarNaFila);
        return () => setPrintQueueInstance(() => { });
    }, []);

    const processarFilaDeImpressao = async () => {
        if (imprimindoAtualmente.current || filaImpressao.current.length === 0) {
            return;
        }

        imprimindoAtualmente.current = true;
        const MAX_TENTATIVAS = 3;

        try {
            await processarFilaSequencial({
                fila: filaImpressao,
                maxTentativas: MAX_TENTATIVAS,
                executar: async job => {
                    switch (job.tipo) {
                        case 'ble':
                            await imprimirBLE(job);
                            break;
                        case 'spp':
                            await imprimirSPP(job);
                            break;
                        case 'rede':
                            await imprimirRede(job);
                            break;
                        case 'local':
                            await imprimirLocal(job);
                            break;
                    }
                },
                aoIniciarTentativa: (job, tentativa, restantes) => {
                    setImprimindoAgora(job.id);
                    console.log(`🖨️ [FILA] Iniciando ${job.tipo.toUpperCase()} (${job.origem}) - Tentativa ${tentativa}/${MAX_TENTATIVAS}`, {
                        id: job.id,
                        restantes
                    });
                },
                aoConcluir: job => {
                    console.log(`✅ [FILA] Concluído (${job.origem}):`, job.id);
                },
                aoFalharTentativa: (job, error, tentativa, _maxTentativas, vaiTentarNovamente) => {
                    const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
                    console.error(`❌ [FILA] Falha (${tentativa}/${MAX_TENTATIVAS}) em ${job.tipo.toUpperCase()}:`, errorMsg);

                    if (vaiTentarNovamente) {
                        const nextDelay = 4000 * tentativa;
                        console.log(`🔄 [FILA] Próximo retry em ${nextDelay / 1000}s...`);
                    }
                },
                aoDescartar: (job, error) => {
                    const entregaIncerta = !!error
                        && typeof error === 'object'
                        && (error as { deliveryUncertain?: boolean }).deliveryUncertain === true;
                    console.log(`🚫 [FILA] Encerrando após ${job.tentativas || 1} tentativa(s) - ${job.tipo.toUpperCase()} (${job.origem})`);
                    if (entregaIncerta) {
                        console.warn(`⚠️ [FILA] Envio com resultado incerto; retry automático bloqueado para evitar duplicidade: ${job.id}`);
                        setAlertaImpressao('A impressora não confirmou o envio. Confira o cupom antes de reimprimir; a fila continuará.');
                    } else {
                        setAlertaImpressao('Não foi possível conectar à impressora. Verifique a conexão; a fila continuará.');
                    }
                },
                aoFinalizarJob: () => setImprimindoAgora(null),
                aoAtualizarFila: fila => setFilaAtual(fila),
            });
        } finally {
            imprimindoAtualmente.current = false;
            setImprimindoAgora(null);

            // Um trabalho pode ter sido adicionado entre o fim do while e a
            // liberação do lock. Nesse caso, inicia um novo worker.
            if (filaImpressao.current.length > 0) {
                void processarFilaDeImpressao();
            }
        }
    };

    const imprimirBLE = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { deviceId } = impressoraConfig;

        if (!deviceId) throw new Error('Device ID não configurado');

        const PRINT_SERVICE_UUID = "000018f0-0000-1000-8000-00805f9b34fb";
        const PRINT_CHARACTERISTIC_UUID = "00002af1-0000-1000-8000-00805f9b34fb";
        const chunkSize = 20;
        const data = new Uint8Array(comandosEscPos);

        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

        // 🔥 CONNECT COM TIMEOUT (FUNCIONA NOS DOIS)
        const connectWithTimeout = (deviceId: string, timeoutMs = 10000) =>
            executarComTimeout(
                () => BleClient.connect(deviceId),
                timeoutMs,
                () => criarErroImpressao(`BLE connect timeout após ${timeoutMs}ms`),
            );

        const stopScanSeguro = async () => {
            try {
                await executarComTimeout(
                    () => BleClient.stopLEScan(),
                    3000,
                    () => criarErroImpressao('Timeout ao encerrar scan BLE'),
                );
            } catch { }
        };

        const disconnectSeguro = async () => {
            try {
                await executarComTimeout(
                    () => BleClient.disconnect(deviceId),
                    5000,
                    () => criarErroImpressao('Timeout ao desconectar BLE'),
                );
            } catch { }
        };

        // Inicializa BLE
        try {
            await executarComTimeout(
                () => BleClient.initialize(),
                5000,
                () => criarErroImpressao('Timeout ao inicializar BLE'),
            );
        } catch { }

        // 🔥 RESET UNIVERSAL (SAFE iOS + ANDROID)
        await stopScanSeguro();
        await disconnectSeguro();

        await delay(700);

        // 🔍 SCAN (mantém robustez)
        console.log(`🔍 [BLE] Escaneando...`);
        await stopScanSeguro();

        await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(async () => {
                await stopScanSeguro();

                try {
                    await executarComTimeout(
                        () => BleClient.getDevices([deviceId]),
                        5000,
                        () => criarErroImpressao('Timeout ao consultar cache BLE'),
                    );
                    console.log(`🔍 [BLE] Fallback via cache`);
                    resolve();
                } catch {
                    reject(new Error('Impressora não encontrada'));
                }
            }, 6000);

            let encontrado = false;

            BleClient.requestLEScan({}, async (result) => {
                if (result.device.deviceId === deviceId && !encontrado) {
                    encontrado = true;
                    clearTimeout(timeout);
                    await stopScanSeguro();
                    resolve();
                }
            }).catch(async () => {
                clearTimeout(timeout);

                try {
                    await executarComTimeout(
                        () => BleClient.getDevices([deviceId]),
                        5000,
                        () => criarErroImpressao('Timeout ao consultar cache BLE'),
                    );
                    resolve();
                } catch {
                    reject(new Error('Scan falhou'));
                }
            });
        });

        await delay(300);

        // 🔌 CONNECT COM RETRY UNIVERSAL
        console.log(`🔌 [BLE] Conectando...`);

        let conectado = false;

        for (let tentativa = 1; tentativa <= 2; tentativa++) {
            try {
                console.log(`🔄 [BLE] Tentativa ${tentativa}`);
                await connectWithTimeout(deviceId);
                conectado = true;
                break;
            } catch (err) {
                console.warn(`⚠️ [BLE] Falha tentativa ${tentativa}`);

                await disconnectSeguro();
                await delay(1200);
            }
        }

        if (!conectado) {
            throw new Error('Falha ao conectar BLE');
        }

        await delay(1000);
        console.log(`✅ [BLE] Conectado`);

        try {
            // RESET ESC/POS
            const reset = new Uint8Array([0x1B, 0x40]);
            await executarComTimeout(
                () => BleClient.write(
                    deviceId,
                    PRINT_SERVICE_UUID,
                    PRINT_CHARACTERISTIC_UUID,
                    new DataView(reset.buffer)
                ),
                8000,
                () => criarErroImpressao('Timeout no reset BLE'),
            );

            await delay(150);

            for (let via = 0; via < quantidadeVias; via++) {
                console.log(`📄 [BLE] Imprimindo via ${via + 1}/${quantidadeVias}`);

                for (let i = 0; i < data.length; i += chunkSize) {
                    const chunk = data.slice(i, i + chunkSize);

                    const safeBuffer = chunk.buffer.slice(
                        chunk.byteOffset,
                        chunk.byteOffset + chunk.byteLength
                    );

                    try {
                        await executarComTimeout(
                            () => BleClient.write(
                                deviceId,
                                PRINT_SERVICE_UUID,
                                PRINT_CHARACTERISTIC_UUID,
                                new DataView(safeBuffer)
                            ),
                            8000,
                            () => criarErroImpressao('Timeout durante envio BLE', {
                                retryable: false,
                                deliveryUncertain: true,
                            }),
                        );
                    } catch (error) {
                        if (!permiteRetryImpressao(error)) throw error;
                        const detalhe = error instanceof Error ? `: ${error.message}` : '';
                        throw criarErroImpressao(`Falha durante envio BLE${detalhe}`, {
                            retryable: false,
                            deliveryUncertain: true,
                        });
                    }

                    await delay(60); // seguro para ambos
                }

                if (via < quantidadeVias - 1) {
                    await delay(2000);
                }
            }

        } finally {
            try {
                await disconnectSeguro();
                console.log(`🔌 [BLE] Desconectado`);
            } catch (error) {
                console.warn(`⚠️ [BLE] Erro ao desconectar:`, error);
            }
        }
    };

    const imprimirSPP = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { address } = impressoraConfig;

        if (!address) throw new Error('Endereço SPP não configurado');

        const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
        const chunkSize = 128;
        const CONNECTION_TIMEOUT_MS = 15000;
        const MAX_RETRY_ATTEMPTS = 3;
        let connectionSubscription: { unsubscribe: () => void } | undefined;

        const cleanupConnectionSubscription = () => {
            if (!connectionSubscription) return;
            connectionSubscription.unsubscribe();
            connectionSubscription = undefined;
        };

        const desconectarSPPSeguro = async () => {
            try {
                await executarComTimeout(
                    () => BluetoothSerial.disconnect(),
                    5000,
                    () => criarErroImpressao('Timeout ao desconectar SPP'),
                );
            } catch { }
        };

        const conectarSPP = (addr: string): Promise<void> =>
            new Promise<void>((resolve, reject) => {
                let settled = false;

                const timeout = setTimeout(() => {
                    if (settled) return;
                    settled = true;
                    cleanupConnectionSubscription();
                    reject(new Error(`Timeout SPP após ${CONNECTION_TIMEOUT_MS}ms`));
                }, CONNECTION_TIMEOUT_MS);

                connectionSubscription = BluetoothSerial.connect(addr).subscribe(
                    () => {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timeout);
                        // No Android, cancelar a subscription aqui aciona um disconnect nativo.
                        resolve();
                    },
                    (error: any) => {
                        if (settled) return;
                        settled = true;
                        clearTimeout(timeout);
                        cleanupConnectionSubscription();
                        reject(error);
                    }
                );
            });

        // Disconnect preventivo — mesma lógica do BLE
        // isConnected() pode retornar estado stale após fechar/abrir o app
        try {
            const isConnected = await executarComTimeout(
                () => BluetoothSerial.isConnected(),
                5000,
                () => criarErroImpressao('Timeout ao consultar conexão SPP'),
            );
            if (isConnected) {
                await desconectarSPPSeguro();
                await delay(500);
                console.log('🧹 [SPP] Sessão anterior encerrada');
            }
        } catch { /* ignorar */ }

        // Conexão com retry interno
        let conectado = false;
        for (let tentativa = 1; tentativa <= MAX_RETRY_ATTEMPTS; tentativa++) {
            try {
                console.log(`🔌 [SPP] Conectando... (tentativa ${tentativa}/${MAX_RETRY_ATTEMPTS})`);
                await conectarSPP(address);
                await delay(1500); // ← delay aqui, fora do callback

                const isConnected = await executarComTimeout(
                    () => BluetoothSerial.isConnected(),
                    5000,
                    () => criarErroImpressao('Timeout ao validar conexão SPP'),
                );
                if (!isConnected) {
                    throw new Error('Conexão SPP foi encerrada antes do envio');
                }

                console.log('✅ [SPP] Conectado');

                await executarComTimeout(
                    () => BluetoothSerial.write('\x1B\x40'),
                    8000,
                    () => criarErroImpressao('Timeout no reset SPP'),
                );
                await delay(100);

                conectado = true;
                break;
            } catch (err) {
                console.warn(`⚠️ [SPP] Falha tentativa ${tentativa}:`, err);
                cleanupConnectionSubscription(); // ← adicionar aqui
                await desconectarSPPSeguro();

                if (tentativa < MAX_RETRY_ATTEMPTS) {
                    const backoff = 1000 * tentativa;
                    console.log(`🔄 [SPP] Aguardando ${backoff}ms antes de reconectar...`);
                    await delay(backoff);
                }
            }
        }

        if (!conectado) throw new Error('Não foi possível conectar à impressora SPP');

        try {
            for (let via = 0; via < quantidadeVias; via++) {
                console.log(`📄 [SPP] Imprimindo via ${via + 1}/${quantidadeVias}`);

                // Envia em chunks para evitar perda de dados em cupons grandes
                for (let i = 0; i < comandosEscPos.length; i += chunkSize) {
                    const chunk = comandosEscPos.slice(i, i + chunkSize);
                    const safeBuffer = chunk.buffer.slice(
                        chunk.byteOffset,
                        chunk.byteOffset + chunk.byteLength
                    );

                    try {
                        await executarComTimeout(
                            () => BluetoothSerial.write(safeBuffer),
                            10000,
                            () => criarErroImpressao('Timeout durante envio SPP', {
                                retryable: false,
                                deliveryUncertain: true,
                            }),
                        );
                    } catch (error) {
                        if (!permiteRetryImpressao(error)) throw error;
                        const detalhe = error instanceof Error ? `: ${error.message}` : '';
                        throw criarErroImpressao(`Falha durante envio SPP${detalhe}`, {
                            retryable: false,
                            deliveryUncertain: true,
                        });
                    }

                    await delay(35);
                }

                if (via < quantidadeVias - 1) await delay(1500);
            }
        } finally {
            try {
                await desconectarSPPSeguro();
                console.log('🔌 [SPP] Desconectado');
            } catch (error) {
                console.warn('⚠️ [SPP] Erro ao desconectar:', error);
            } finally {
                cleanupConnectionSubscription();
            }
        }
    };

    const imprimirRede = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { ip, porta } = impressoraConfig;

        if (!ip || !porta) {
            throw new Error('IP/Porta não configurados');
        }

        const tcp = (window as any).chrome?.sockets?.tcp as TcpSocketApi | undefined;

        for (let via = 0; via < quantidadeVias; via++) {
            console.log(`📄 [REDE] Imprimindo via ${via + 1}/${quantidadeVias}`);

            await enviarBufferTcp({
                tcp,
                ip,
                porta,
                data: comandosEscPos.buffer as ArrayBuffer,
                timeoutMs: 10000,
            });

            if (via < quantidadeVias - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    };

    const imprimirLocal = async (job: PrintJob) => {
        const { comandosEscPos, impressoraConfig, quantidadeVias } = job;
        const { printerName } = impressoraConfig;

        if (!printerName) {
            throw new Error('Nome da impressora não configurado');
        }

        const dataString = Array.from(comandosEscPos)
            .map(byte => String.fromCharCode(byte))
            .join('');

        const enviarParaImpressora = async () => {
            const controller = new AbortController();
            let response: Response;

            try {
                response = await executarComTimeout(
                    () => fetch("http://localhost:9100/print", {
                        method: 'POST',
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ printer: printerName, data: dataString }),
                        signal: controller.signal,
                    }),
                    15000,
                    () => {
                        controller.abort();
                        return criarErroImpressao('Timeout durante impressão Local', {
                            retryable: false,
                            deliveryUncertain: true,
                        });
                    },
                );
            } catch (error) {
                if (!permiteRetryImpressao(error)) throw error;
                const detalhe = error instanceof Error ? `: ${error.message}` : '';
                throw criarErroImpressao(`Falha durante impressão Local${detalhe}`, {
                    retryable: false,
                    deliveryUncertain: true,
                });
            }

            if (!response.ok) {
                throw criarErroImpressao(`Falha impressão Local (HTTP ${response.status})`, {
                    retryable: false,
                    deliveryUncertain: true,
                });
            }
        };

        for (let via = 0; via < quantidadeVias; via++) {
            console.log(`📄 [LOCAL] Imprimindo via ${via + 1}/${quantidadeVias}`);
            await enviarParaImpressora();

            if (via < quantidadeVias - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        }
    };

    const limparFila = () => {
        filaImpressao.current = [];
        setFilaAtual([]);
        console.log(`🗑️ [FILA] Limpa`);
    };

    return (
        <>
            <PrintQueueContext.Provider
                value={{
                    adicionarNaFila,
                    filaAtual,
                    imprimindoAgora,
                    limparFila
                }}
            >
                {children}
            </PrintQueueContext.Provider>
            <IonToast
                isOpen={alertaImpressao !== null}
                message={alertaImpressao || ''}
                duration={7000}
                position="top"
                color="danger"
                onDidDismiss={() => setAlertaImpressao(null)}
            />
        </>
    );
};

export const usePrintQueue = () => {
    const context = useContext(PrintQueueContext);
    if (!context) {
        throw new Error('usePrintQueue deve ser usado dentro de PrintQueueProvider');
    }
    return context;
};
